import { IntegrationDescriptionSampleTemplates } from '@modules/integrationsV2/components/IntegrationDescriptionForm/IntegrationDescriptionSampleTemplates';
import { IntegrationDescriptionValidationQuestions } from '@modules/integrationsV2/components/IntegrationDescriptionForm/IntegrationDescriptionValidationQuestions';
import { MAX_DESCRIPTION_ALLOWED_CHARS } from '@modules/integrationsV2/hooks/useValidateIntegrationForm';
import { Box, Link, Stack, TextField, Typography } from '@mui/material';
import { IntegrationType, ValidForQuestion } from 'generated/graphql';

interface IntegrationDescriptionFormProps {
  value: string;
  onChange: (value: string) => void;
  descriptionValidForQuestions: ValidForQuestion[];
  isValidationInProgress: boolean;
  isRequired: boolean;
  validatedDescriptionMessage: string;
  isPreValidated: boolean;
  integrationType: IntegrationType;
}

export const IntegrationDescriptionForm = ({
  value,
  onChange,
  descriptionValidForQuestions,
  isValidationInProgress,
  isRequired,
  validatedDescriptionMessage,
  isPreValidated,
  integrationType,
}: IntegrationDescriptionFormProps) => {
  if (integrationType !== IntegrationType.Feedback) {
    return null;
  }

  const onChangeDescription = (value: string) => {
    onChange(value);
  };

  const isEmptyState =
    !isPreValidated && descriptionValidForQuestions.length === 0;

  const isMaxCharsLimit = value.length > MAX_DESCRIPTION_ALLOWED_CHARS;

  const getHelperText = () => {
    const charCount = `${value.length}/${MAX_DESCRIPTION_ALLOWED_CHARS}`;
    if (!isEmptyState && validatedDescriptionMessage) {
      return validatedDescriptionMessage;
    }
    return isMaxCharsLimit ? `${charCount} (max limit reached)` : charCount;
  };

  const isInvalidDescription =
    !isEmptyState && !isPreValidated
      ? descriptionValidForQuestions.some((question) => !question.isValid)
      : false;

  const isLoading = isValidationInProgress || (isEmptyState && !!value);

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="subtitle1">
          Describe the type of feedback being integrated
          {isRequired && (
            <Box component="span" color="error.main">
              *
            </Box>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This description is used to enhance the quality of summaries and
          predictions for this integration.{' '}
          <Link
            underline="hover"
            href="https://helpcenter.enterpret.com/en/articles/8999488-integration-descriptions"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </Link>
        </Typography>
      </Stack>
      <Stack spacing={2}>
        <IntegrationDescriptionSampleTemplates
          description={value}
          onSelect={(description) => onChangeDescription(description)}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          variant="outlined"
          placeholder="Choose a template from above, or enter a description..."
          value={value}
          onChange={(e) => onChangeDescription(e.target.value)}
          helperText={getHelperText()}
          error={isMaxCharsLimit}
          sx={{
            fieldset: {
              borderColor: isInvalidDescription ? 'warning.main' : undefined,
            },
            '.MuiFormHelperText-root': {
              color:
                isInvalidDescription && validatedDescriptionMessage
                  ? 'warning.main'
                  : undefined,
            },
          }}
        />
      </Stack>
      <IntegrationDescriptionValidationQuestions
        isLoading={isLoading}
        isEmptyState={isEmptyState}
        validForQuestions={descriptionValidForQuestions}
        shouldShowAsValidated={isPreValidated}
      />
    </Stack>
  );
};
